'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './creative.css';

/**
 * "Her kanal için doğru ölçü" — görselin ilk sorusu.
 *
 * Kanal → boyut eşlemesi `lib/image-channels.ts` → `IMAGE_CHANNEL_SPECS`in
 * AYNASI. Oradan import edilmiyor: o dosya `@mentorivo/shared` kökünü
 * içeri alıyor ve tanıtım sayfasının istemci paketine bütün sözleşme
 * katmanını taşırdı. Kanallar ya da ölçüler değişirse ikisi BİRLİKTE değişir.
 *
 * Sağdaki çizim ölçekli: kanalın boyutları birbirine göre gerçek oranlarında
 * (en büyük kenar = tuvalin tavanı). Google banner'da 728×90 ile 160×600 aynı
 * tuvalde — "kendiniz kırpmayın" sözünü en iyi o anlatıyor.
 *
 * Hareket: kanal değişince kareler yeni boyutlarına KAYIYOR (`layout` +
 * ortak `layoutId` — boyut değişimi transform'a çevriliyor, DESIGN.md §1).
 * Kanallar arasında ortak boyut (1080×1080) yerinde kalıp yeniden
 * boyutlanıyor; yeni gelenler bulanıktan netleşiyor.
 */

const CHANNELS = [
  { key: 'meta_feed', marks: ['instagram', 'facebook'], sizes: ['1080x1350', '1080x1080'] },
  { key: 'meta_stories', marks: ['instagram', 'facebook'], sizes: ['1080x1920'] },
  { key: 'tiktok', marks: ['tiktok'], sizes: ['1080x1920'] },
  { key: 'google_display', marks: ['google'], sizes: ['1200x628', '1080x1080'] },
  { key: 'google_banner', marks: ['google'], sizes: ['300x250', '728x90', '160x600', '320x50'] },
  { key: 'linkedin_feed', marks: ['linkedin'], sizes: ['1080x1080', '1200x628'] },
  { key: 'x_feed', marks: ['x'], sizes: ['1200x628', '1080x1080'] },
] as const;
type ChannelKey = (typeof CHANNELS)[number]['key'];

const FORMATS = ['single', 'series', 'carousel', 'pack'] as const;
const VIDEO_RATIOS = ['9:16', '4:5', '1:1', '16:9'] as const;

/** Tuvalin en/boy oranı (CSS `.crc-canvas` ile aynı), kare arası boşluk ve
 *  en uzun karenin tuval yüksekliğindeki payı. */
const CANVAS_RATIO = 16 / 10;
const GAP = 0.04;
const MAX_H = 0.82;

const parse = (size: string) => {
  const [w, h] = size.split('x').map(Number);
  return { w: w!, h: h! };
};

export function CreativeChannels() {
  const t = useTranslations('featuresCreative.channels');
  const uid = useId();
  const [active, setActive] = useState<ChannelKey>('meta_feed');
  const channel = CHANNELS.find((c) => c.key === active)!;

  /* Ölçek: kareler yan yana, aralarında tuval genişliğinin %4'ü. Tek bir
     piksel ölçeği hem genişliğe (toplam genişlik sığsın) hem yüksekliğe (en
     uzun kare tuvalin %82'sini aşmasın) uyuyor — 9:16 tek kare taşmıyor,
     Google banner'ın dört boyu aynı ölçekte yan yana duruyor. Tuval oranı
     CSS'teki `.crc-canvas` ile aynı (16:10). */
  const dims = channel.sizes.map(parse);
  const avail = 1 - GAP * (dims.length - 1);
  const sumW = dims.reduce((total, d) => total + d.w, 0);
  const maxH = Math.max(...dims.map((d) => d.h));
  const scale = Math.min(avail / sumW, MAX_H / (maxH * CANVAS_RATIO));

  return (
    <ProductSection tone="gray" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="crc">
        <ul className="crc-list" aria-label={t('listLabel')}>
          {CHANNELS.map(({ key, marks }) => (
            <li key={key}>
              <button
                type="button"
                className="crc-channel"
                aria-pressed={active === key}
                aria-controls={`${uid}-board`}
                onClick={() => setActive(key)}
              >
                <span className="crc-marks" aria-hidden="true">
                  {marks.map((mark) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={mark}
                      className="crc-mark"
                      src={`/logos/brands/${mark}.svg`}
                      alt=""
                      width={20}
                      height={20}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </span>
                <span className="crc-name">{t(`items.${key}`)}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="crc-board" id={`${uid}-board`} aria-live="polite">
          <p className="crc-board-head">
            {t(`items.${active}`)} · {t('sizeCount', { count: channel.sizes.length })}
          </p>
          <div className="crc-canvas">
            <AnimatePresence mode="popLayout" initial={false}>
              {channel.sizes.map((size, i) => {
                const { w, h } = dims[i]!;
                return (
                  <motion.figure
                    key={size}
                    layoutId={`${uid}-${size}`}
                    layout
                    className="crc-frame"
                    style={{
                      width: `${w * scale * 100}%`,
                      height: `${h * scale * CANVAS_RATIO * 100}%`,
                    }}
                    initial={{ opacity: 0, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, transition: { duration: dur.instant } }}
                    transition={springFade(spring.default)}
                  >
                    {/* `layout`: kare boyut değiştirirken yazı ezilmesin —
                        Motion ölçeği çocukta geri çeviriyor. */}
                    <motion.figcaption layout className="crc-frame-size">
                      {w}×{h}
                    </motion.figcaption>
                  </motion.figure>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="crc-more">
        <div className="crc-more-block">
          <p className="crc-more-label">{t('formatsLabel')}</p>
          <ul className="crc-chips">
            {FORMATS.map((key) => (
              <li key={key} className="crc-chip">
                <span className="crc-chip-name">{t(`formats.${key}.name`)}</span>
                <span className="crc-chip-note">{t(`formats.${key}.note`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="crc-more-block">
          <p className="crc-more-label">{t('videoLabel')}</p>
          <ul className="crc-ratios">
            {VIDEO_RATIOS.map((ratio) => (
              <li key={ratio} className="crc-ratio">
                <span className="crc-ratio-box" style={{ aspectRatio: ratio.replace(':', ' / ') }} />
                {ratio}
              </li>
            ))}
          </ul>
          <p className="crc-video-note">{t('videoNote')}</p>
        </div>
      </div>
    </ProductSection>
  );
}
